import type { Node } from '@react-types/shared';
import cn from 'classnames';
import React, { useRef } from 'react';
import { mergeProps, useGridListItem, useHover } from 'react-aria';
import { ListState } from 'react-stately';
import { createBlockheadClassName } from 'utils/classnames';
import { ListItemProvider } from './ListItemContext';
import * as S from './styles';

export interface ListItemProps {
  item: Node<unknown>;
  showDivider?: boolean;
  state: ListState<unknown>;
}

export const LIST_ITEM_CLASSNAME = createBlockheadClassName('ListItem');
export const LIST_ITEM_GRID_CELL_CLASSNAME = createBlockheadClassName(
  'ListItem',
  'GridCell',
);

const ListItem: React.FC<ListItemProps> = ({ item, showDivider, state }) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const { isDisabled, gridCellProps, rowProps } = useGridListItem(
    { node: item },
    state,
    itemRef,
  );
  const { hoverProps, isHovered } = useHover({
    isDisabled: state.disabledKeys.has(item.key),
  });

  const { className: gridCellClassName, ...restGridCellProps } = gridCellProps;

  return (
    <S.Root
      {...mergeProps(hoverProps, rowProps)}
      className={LIST_ITEM_CLASSNAME}
      ref={itemRef}
      data-hovered={isHovered}
    >
      <S.GridCell
        className={cn(gridCellClassName, LIST_ITEM_GRID_CELL_CLASSNAME)}
        {...restGridCellProps}
      >
        <ListItemProvider isDisabled={isDisabled}>
          {item.rendered}
        </ListItemProvider>
      </S.GridCell>
      {item.nextKey !== undefined && showDivider && <S.RowDivider />}
    </S.Root>
  );
};

export default ListItem;
